// The Vendors tab (F-FE-N93). One directory of vendors, each opening a
// profile with everything about them.
//
// Replaces three surfaces that split one vendor across three tabs: the
// Vendors / AP aging + bills tables, the Vendor Reports tab (whose first
// control was "pick a vendor"), and the Suppliers card inside Purchasing.
// Nothing here is new data — it is the same endpoints, arranged around the
// vendor instead of around the accounting function.
import { useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { Search, Plus, Truck, ChevronRight } from 'lucide-react'
import apiClient from '@/api/client'
import type { Supplier, VendorBill } from '@/types'
import { useCurrency } from '@/contexts/CurrencyContext'
import { compactCurrency } from '@/components/admin/dashboard/dashboardFormat'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { cn } from '@/lib/utils'
import { getStoredUserRole } from '@/lib/staff-roles'
import { VendorProfileSheet } from './VendorProfileSheet'
import { vendorStatusFromBills, isOverdue, isDueThisWeek, type VendorTone, vendorNetOwed } from './vendorStatus'
import {
  describeOpeningBalance, findOpeningBalanceBill, localISODate, parseOpeningBalanceInput, shortDayMonthYear,
} from './vendorOpeningBalance'
import type { InventorySearch } from './inventoryRouteSearch'

type ToastFn = (type: 'success' | 'error', message: string) => void
type Filter = 'all' | 'owed' | 'late' | 'consignment'

const TONE_CLASS: Record<VendorTone, string> = {
  danger: 'bg-danger-surface text-danger-fg border-danger-border',
  warning: 'bg-warning-surface text-warning-fg border-warning-border',
  success: 'bg-success-surface text-success-fg border-success-border',
  info: 'bg-info-surface text-info-fg border-info-border',
  neutral: 'bg-neutral-surface text-neutral-fg border-neutral-border',
}

function StatusPill({ tone, text }: { tone: VendorTone; text: string }) {
  return (
    <span className={cn('inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium', TONE_CLASS[tone])}>
      <span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden="true" />
      {text}
    </span>
  )
}

function Tile({ label, value, sub, alarm }: { label: string; value: string; sub?: string; alarm?: boolean }) {
  return (
    <Card className={cn(alarm && 'border-danger-border bg-danger-surface')}>
      <CardContent className="flex flex-col gap-1 px-4 py-3.5">
        <span className={cn('text-[11px] font-medium uppercase tracking-wide', alarm ? 'text-danger-fg/85' : 'text-muted-foreground')}>
          {label}
        </span>
        <span className={cn('text-2xl font-semibold tabular-nums', alarm && 'text-danger-fg')}>{value}</span>
        {sub && <span className={cn('text-xs', alarm ? 'text-danger-fg/90' : 'text-muted-foreground')}>{sub}</span>}
      </CardContent>
    </Card>
  )
}

export function VendorsDirectory({ showToast }: { showToast: ToastFn }) {
  const qc = useQueryClient()
  const { formatCurrency } = useCurrency()
  const navigate = useNavigate()
  const search = useSearch({ strict: false }) as InventorySearch
  // F-MM-N24: recording an opening balance creates a bill, and bills are
  // admin-only (storeAdmin) — so the section is hidden from everyone else
  // rather than rendered into a guaranteed 403 (the F-FE-N117 lesson).
  const isAdmin = getStoredUserRole() === 'admin'

  const [query, setQuery] = useState('')
  const [filter, setFilter] = useState<Filter>('all')

  const { data: supRes } = useQuery({ queryKey: ['suppliers'], queryFn: () => apiClient.listSuppliers() })
  const suppliers: Supplier[] = supRes?.data ?? []

  const { data: billsRes } = useQuery({ queryKey: ['vendorBills'], queryFn: () => apiClient.listVendorBills() })
  const bills: VendorBill[] = billsRes?.data ?? []

  const billsBySupplier = useMemo(() => {
    const m = new Map<string, VendorBill[]>()
    for (const b of bills) {
      const list = m.get(b.supplier_id)
      if (list) list.push(b)
      else m.set(b.supplier_id, [b])
    }
    return m
  }, [bills])

  // ── Summary. Every figure here comes from the bills already on screen, so
  // the strip can never disagree with the rows beneath it.
  const summary = useMemo(() => {
    // F-MM-N21: "You owe" nets each vendor's unused supplier credit, floored
    // at zero per vendor — a credit with one vendor never pays another.
    const net = suppliers.map(s => vendorNetOwed(billsBySupplier.get(s.id) ?? [], s.credit_balance))
    const owed = net.reduce((sum, n) => sum + Math.max(0, n), 0)
    const owedVendors = net.filter(n => n > 0.01).length
    const overdueBills = bills.filter(isOverdue)
    const overdue = overdueBills.reduce((s, b) => s + b.outstanding, 0)
    const overdueVendors = new Set(overdueBills.map(b => b.supplier_id)).size
    const week = bills.filter(isDueThisWeek).reduce((s, b) => s + b.outstanding, 0)
    const drafts = bills.filter(b => b.status === 'draft').length
    return { owed, overdue, overdueVendors, week, drafts, owedVendors }
  }, [bills, suppliers, billsBySupplier])

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase()
    return suppliers
      .map(s => {
        const own = billsBySupplier.get(s.id) ?? []
        const status = vendorStatusFromBills(own)
        const net = vendorNetOwed(own, s.credit_balance)
        const owed = Math.max(0, net)
        const credit = Math.max(0, -net)
        const lastBill = own.reduce<string | undefined>((latest, b) => {
          const d = b.bill_date ?? b.created_at.slice(0, 10)
          return !latest || d > latest ? d : latest
        }, undefined)
        return { supplier: s, status, owed, credit, lastBill }
      })
      .filter(r => {
        if (q && !r.supplier.name.toLowerCase().includes(q)
          && !(r.supplier.contact_name ?? '').toLowerCase().includes(q)
          && !(r.supplier.phone ?? '').toLowerCase().includes(q)) return false
        if (filter === 'owed' && r.owed <= 0.01) return false
        if (filter === 'late' && r.status.tone !== 'danger') return false
        if (filter === 'consignment' && !r.supplier.is_consignment) return false
        return true
      })
      // Most urgent first: latest overdue at the top, settled vendors last.
      .sort((a, b) => b.status.daysLate - a.status.daysLate || b.owed - a.owed || a.supplier.name.localeCompare(b.supplier.name))
  }, [suppliers, billsBySupplier, query, filter])

  // The inventory route's whole search surface is {tab, vendor}, so these
  // build it outright rather than spreading `prev` (which TanStack types as
  // the union of every route's search).
  const openVendor = (id: string) => {
    navigate({ to: '/admin/inventory', search: { tab: 'vendors', vendor: id }, replace: true })
  }
  const closeVendor = () => {
    navigate({ to: '/admin/inventory', search: { tab: 'vendors' }, replace: true })
  }

  const activeVendor = suppliers.find(s => s.id === search.vendor) ?? null

  // ── Vendor create / edit / delete (moved here from Purchasing, where a
  // vendor's contact details lived a tab away from their money) ────────────
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<Supplier | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<Supplier | null>(null)
  const emptyForm = () => ({
    name: '', contact_name: '', phone: '', email: '', commission_percent: '', is_consignment: false,
    // F-MM-N24: what you already owed them when you started on Bhookly.
    opening_amount: '', opening_as_of: localISODate(), opening_note: '',
  })
  const [form, setForm] = useState(emptyForm)

  const commissionValue = () => {
    const n = parseFloat(form.commission_percent)
    return Number.isFinite(n) ? n : 0
  }
  const resetForm = () => setForm(emptyForm())
  const openCreate = () => { setEditing(null); resetForm(); setDialogOpen(true) }
  const openEdit = (s: Supplier) => {
    setEditing(s)
    setForm({
      name: s.name,
      contact_name: s.contact_name ?? '',
      phone: s.phone ?? '',
      email: s.email ?? '',
      commission_percent: s.commission_percent != null ? String(s.commission_percent) : '',
      is_consignment: !!s.is_consignment,
      opening_amount: '', opening_as_of: localISODate(), opening_note: '',
    })
    setDialogOpen(true)
  }

  // The vendor being edited already has an opening balance → show it, don't
  // ask for another (the backend refuses a second one anyway).
  const existingOpening = editing ? findOpeningBalanceBill(billsBySupplier.get(editing.id) ?? []) : null
  const openingEntered = isAdmin && !existingOpening && form.opening_amount.trim() !== ''
  const openingParse = openingEntered
    ? parseOpeningBalanceInput({ amount: form.opening_amount, as_of: form.opening_as_of, note: form.opening_note }, localISODate())
    : null

  const saveVendor = useMutation({
    mutationFn: async () => {
      const payload = {
        name: form.name,
        contact_name: form.contact_name || undefined,
        phone: form.phone || undefined,
        email: form.email || undefined,
        commission_percent: commissionValue(),
        is_consignment: form.is_consignment,
      }
      const res = editing ? await apiClient.updateSupplier(editing.id, payload) : await apiClient.createSupplier(payload)
      // F-MM-N24: the opening balance is its own admin-only call. If it
      // fails after the vendor was saved, say exactly that — the vendor
      // exists, and the balance can be added from Edit vendor.
      if (openingParse?.ok) {
        const supplierId = editing ? editing.id : (res as { data?: { id?: string } }).data?.id
        if (supplierId) {
          try {
            await apiClient.createSupplierOpeningBalance(supplierId, openingParse.payload)
          } catch (e) {
            const msg = e instanceof Error ? e.message : 'request failed'
            throw new Error(`Vendor saved, but the opening balance was not recorded: ${msg}`)
          }
        }
      }
      return res
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['suppliers'] })
      // F-FE-N118: supplier_name is denormalised onto items, alerts and orders.
      qc.invalidateQueries({ queryKey: ['stockItems'] })
      qc.invalidateQueries({ queryKey: ['stockAlerts'] })
      qc.invalidateQueries({ queryKey: ['purchaseOrders'] })
      if (openingParse?.ok) {
        qc.invalidateQueries({ queryKey: ['vendorBills'] })
        qc.invalidateQueries({ queryKey: ['vendorLedger'] })
        qc.invalidateQueries({ queryKey: ['inventoryActivity'] })
      }
      showToast('success', editing ? 'Vendor updated' : 'Vendor added')
      setDialogOpen(false)
      setEditing(null)
      resetForm()
    },
    onError: (e: Error) => {
      // The vendor may have been saved before the balance failed.
      qc.invalidateQueries({ queryKey: ['suppliers'] })
      showToast('error', e.message || 'Failed to save vendor')
    },
  })

  const removeVendor = useMutation({
    mutationFn: () => {
      if (!deleteTarget) throw new Error('No vendor selected')
      return apiClient.deleteSupplier(deleteTarget.id)
    },
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['suppliers'] })
      qc.invalidateQueries({ queryKey: ['inventoryActivity'] })
      qc.invalidateQueries({ queryKey: ['stockItems'] })
      qc.invalidateQueries({ queryKey: ['stockAlerts'] })
      qc.invalidateQueries({ queryKey: ['purchaseOrders'] })
      showToast('success', res.message || 'Vendor deleted')
      setDeleteTarget(null)
    },
    onError: (e: Error) => showToast('error', e.message || 'Failed to delete vendor'),
  })

  const vendorShare = Math.round((100 - commissionValue()) * 100) / 100

  if (suppliers.length === 0) {
    return (
      <>
        <div className="rounded-lg border border-dashed bg-muted/20 p-10 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
            <Truck className="h-6 w-6" />
          </div>
          <h3 className="mt-3 text-base font-semibold">No vendors yet</h3>
          <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
            Add the businesses you buy from. Once a vendor exists you can raise purchase orders,
            track what you owe them, and send them a statement.
          </p>
          <Button className="mt-4" onClick={openCreate}><Plus className="mr-1 h-4 w-4" /> Add your first vendor</Button>
        </div>
        {renderVendorDialog()}
      </>
    )
  }

  return (
    <div className="space-y-5 pb-12">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Tile
          label="You owe"
          value={summary.owed > 0 ? compactCurrency(summary.owed) : '—'}
          sub={summary.owedVendors ? `across ${summary.owedVendors} vendor${summary.owedVendors === 1 ? '' : 's'}` : 'nothing outstanding'}
        />
        <Tile
          label="Overdue"
          value={summary.overdue > 0 ? formatCurrency(summary.overdue) : 'None'}
          sub={summary.overdue > 0 ? `${summary.overdueVendors} vendor${summary.overdueVendors === 1 ? '' : 's'} past due` : 'nothing past due'}
          alarm={summary.overdue > 0}
        />
        <Tile
          label="Due this week"
          value={summary.week > 0 ? formatCurrency(summary.week) : '—'}
          sub={summary.week > 0 ? 'falls due within 7 days' : 'nothing due this week'}
        />
        <Tile
          label="Bills to confirm"
          value={String(summary.drafts)}
          sub={summary.drafts ? 'checked against their invoice yet?' : 'all bills confirmed'}
        />
      </div>

      <Card>
        <div className="flex flex-wrap items-center gap-2.5 px-4 py-3.5">
          <div className="relative w-full max-w-xs flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="search"
              className="h-10 pl-9"
              placeholder="Search vendors…"
              aria-label="Search vendors"
              value={query}
              onChange={e => setQuery(e.target.value)}
            />
          </div>
          <div className="flex items-center gap-1 rounded-md bg-muted p-1" role="group" aria-label="Filter vendors">
            {([['all', 'All'], ['owed', 'You owe'], ['late', 'Overdue'], ['consignment', 'Consignment']] as [Filter, string][]).map(([id, label]) => (
              <Button
                key={id}
                size="sm"
                variant={filter === id ? 'default' : 'ghost'}
                className="h-7 px-3 text-xs"
                aria-pressed={filter === id}
                onClick={() => setFilter(id)}
              >
                {label}
              </Button>
            ))}
          </div>
          <div className="ml-auto">
            <Button size="sm" className="h-10" onClick={openCreate}><Plus className="mr-1 h-4 w-4" /> Add vendor</Button>
          </div>
        </div>

        <div className="overflow-x-auto border-t">
          <table className="w-full min-w-[720px] text-left text-sm">
            <thead className="border-b bg-muted/50 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-2.5 font-semibold">Vendor</th>
                <th className="px-4 py-2.5 font-semibold">Contact</th>
                <th className="px-4 py-2.5 text-right font-semibold">You owe</th>
                <th className="px-4 py-2.5 font-semibold">Status</th>
                <th className="px-4 py-2.5 font-semibold">Last bill</th>
                <th className="px-4 py-2.5" />
              </tr>
            </thead>
            <tbody className="divide-y">
              {rows.map(r => (
                <tr
                  key={r.supplier.id}
                  role="button"
                  tabIndex={0}
                  aria-label={`Open ${r.supplier.name}`}
                  className="group cursor-pointer hover:bg-muted/50 focus-visible:bg-muted/50 focus-visible:outline-none"
                  onClick={() => openVendor(r.supplier.id)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openVendor(r.supplier.id) }
                  }}
                >
                  <td className="px-4 py-3">
                    <span className="flex items-center gap-2 font-medium">
                      {r.supplier.name}
                      {!!r.supplier.is_consignment && (
                        <Badge variant="info-surface" className="text-[11px]">
                          Consignment {r.supplier.commission_percent ?? 0}%
                        </Badge>
                      )}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">
                    {r.supplier.contact_name || r.supplier.phone || <span className="opacity-60">—</span>}
                  </td>
                  <td className="px-4 py-3 text-right font-semibold tabular-nums">
                    {r.owed > 0.01
                      ? formatCurrency(r.owed)
                      : r.credit > 0.01
                        ? <span className="font-normal text-muted-foreground">{formatCurrency(r.credit)} credit</span>
                        : <span className="font-normal text-muted-foreground">—</span>}
                  </td>
                  <td className="px-4 py-3"><StatusPill tone={r.status.tone} text={r.status.text} /></td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">{r.lastBill ?? '—'}</td>
                  <td className="px-4 py-3 text-right">
                    <ChevronRight className="ml-auto h-4 w-4 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {rows.length === 0 && (
          <div className="border-t px-6 py-10 text-center">
            <p className="text-sm font-medium">No vendors match</p>
            <p className="mt-1 text-sm text-muted-foreground">Try a different search, or clear the filters.</p>
            <Button variant="outline" size="sm" className="mt-3" onClick={() => { setQuery(''); setFilter('all') }}>
              Clear filters
            </Button>
          </div>
        )}
      </Card>

      <VendorProfileSheet
        vendor={activeVendor}
        open={!!activeVendor}
        onClose={closeVendor}
        onEdit={openEdit}
        onDelete={setDeleteTarget}
        showToast={showToast}
      />

      {renderVendorDialog()}

      <Dialog open={!!deleteTarget} onOpenChange={o => !o && setDeleteTarget(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl">Delete vendor</DialogTitle>
            <DialogDescription>
              Delete &quot;{deleteTarget?.name}&quot;? Vendors already linked to purchase orders cannot be deleted —
              the server blocks it so your purchase history stays intact.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button variant="destructive" disabled={removeVendor.isPending} onClick={() => removeVendor.mutate()}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )

  function renderVendorDialog() {
    return (
      <Dialog open={dialogOpen} onOpenChange={o => { setDialogOpen(o); if (!o) { setEditing(null); resetForm() } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl">{editing ? 'Edit vendor' : 'Add vendor'}</DialogTitle>
            <DialogDescription>
              A business you buy from. Purchase orders, bills and statements all hang off this record.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Contact person</Label>
                <Input value={form.contact_name} onChange={e => setForm(f => ({ ...f, contact_name: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input
                  value={form.phone}
                  placeholder="+92 300 1234567"
                  onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
            </div>

            <div className="space-y-2 rounded-lg border bg-muted/30 p-3">
              <label className="flex items-start gap-2.5 text-sm">
                <input
                  type="checkbox"
                  className="mt-1 h-4 w-4"
                  checked={form.is_consignment}
                  onChange={e => setForm(f => ({ ...f, is_consignment: e.target.checked }))}
                />
                <span>
                  <span className="font-medium">They stock it, you sell it (consignment)</span>
                  <span className="mt-0.5 block text-sm text-muted-foreground">
                    You don&apos;t buy their goods up front. When an item sells, they get their share and you
                    keep your commission — so receiving a delivery creates no cost bill. You settle them
                    from sales instead.
                  </span>
                </span>
              </label>
              {form.is_consignment && (
                <div className="space-y-2 pt-1">
                  <Label>Your share of their sales</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      className="w-28 tabular-nums"
                      value={form.commission_percent}
                      onChange={e => setForm(f => ({ ...f, commission_percent: e.target.value }))}
                    />
                    <span className="text-sm text-muted-foreground">
                      % to you · <span className="font-medium text-foreground">{vendorShare}%</span> to them
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* F-MM-N24: opening balance brought forward. Admin-only — it
                creates a bill. Once recorded it is shown here read-only and
                corrected from the vendor's Unpaid bills like any other. */}
            {isAdmin && (
              <div className="space-y-2 rounded-lg border bg-muted/30 p-3">
                {existingOpening ? (() => {
                  const d = describeOpeningBalance(existingOpening, formatCurrency, shortDayMonthYear)
                  return (
                    <>
                      <div className="text-sm font-medium">Opening balance</div>
                      <div className="text-sm tabular-nums">{d.value}</div>
                      <div className="text-sm text-muted-foreground">
                        {d.sub} · edit or void it from this vendor&apos;s Unpaid bills.
                      </div>
                    </>
                  )
                })() : (
                  <>
                    <div className="text-sm font-medium">
                      Opening balance <span className="text-sm font-normal text-muted-foreground">(optional)</span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      What you already owed them when you started on Bhookly. It becomes an open bill you
                      pay down like any other, and their statement carries it forward.
                    </p>
                    <div className="grid grid-cols-2 gap-3 pt-1">
                      <div className="space-y-2">
                        <Label>Amount owed</Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          inputMode="decimal"
                          className="tabular-nums"
                          value={form.opening_amount}
                          placeholder="0.00"
                          onChange={e => setForm(f => ({ ...f, opening_amount: e.target.value }))}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>As of</Label>
                        <Input
                          type="date"
                          max={localISODate()}
                          value={form.opening_as_of}
                          onChange={e => setForm(f => ({ ...f, opening_as_of: e.target.value }))}
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Reference</Label>
                      <Input
                        value={form.opening_note}
                        placeholder="Optional, e.g. balance per old ledger"
                        onChange={e => setForm(f => ({ ...f, opening_note: e.target.value }))}
                      />
                    </div>
                    {openingParse && !openingParse.ok && (
                      <p className="text-sm text-danger-fg">{openingParse.reason}</p>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button
              disabled={saveVendor.isPending || !form.name.trim() || (openingParse != null && !openingParse.ok)}
              onClick={() => saveVendor.mutate()}
            >
              {editing ? 'Save changes' : 'Add vendor'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )
  }
}
